import { Badge, styled } from '@mui/material'

import { theme } from 'styles/theme'

export const Wrapper = styled('div')(() => ({
  alignItems: 'center',
  borderRadius: '50%',
  cursor: 'pointer',
  display: 'flex',
  height: '32px',
  justifyContent: 'center',
  position: 'relative',
  width: '32px'
}))

export const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error[300],
    color: '#fff',
    fontSize: '10px',
    fontWeight: 700,
    minWidth: '18px',
    height: '18px',
    padding: '0 5px',
    right: -1,
    top: -1,
    border: `2px solid ${theme.palette.neutral.A200}`,
    borderRadius: '10px',
    letterSpacing: '-0.2px'
  }
}))

export const DrawerHeaderWrapper = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: '8px',
  [theme.breakpoints.down('sm')]: {
    paddingRight: '8px'
  }
}))

export const HeaderLeft = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px'
}))

export const HeaderActions = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
}))

export const UnreadBadge = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.error[300],
  color: '#fff',
  fontSize: '11px',
  fontWeight: 700,
  borderRadius: '10px',
  padding: '1px 8px',
  minWidth: '22px',
  height: '20px',
  letterSpacing: '-0.2px',
  lineHeight: 1
}))

export const MarkAllReadButton = styled('button')(() => ({
  all: 'unset',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  padding: '5px 10px',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: 500,
  fontFamily: 'Inter, sans-serif',
  color: theme.palette.primary[100],
  backgroundColor: 'transparent',
  transition: 'background-color 150ms linear',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: theme.palette.neutral[800]
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.4
  },
  [theme.breakpoints.down('sm')]: {
    padding: '6px',
    '& .mark-all-label': {
      display: 'none'
    }
  }
}))

export const FiltersWrapper = styled('div')(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '6px',
  padding: '12px 16px',
  flexShrink: 0,
  borderBottom: `1px solid ${theme.palette.neutral[700]}`,
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: '10px 12px',
    gap: '8px',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  }
}))

interface FilterChipProps {
  isActive: boolean
  chipColor?: string
}

export const FilterChip = styled('button')<FilterChipProps>(
  ({ isActive, chipColor }) => ({
    all: 'unset',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    padding: '4px 10px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: isActive ? 600 : 400,
    fontFamily: 'Inter, sans-serif',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flexShrink: 0,
      padding: '6px 12px',
      fontSize: '13px'
    },
    color: isActive
      ? chipColor || theme.palette.primary[100]
      : theme.palette.neutral[400],
    backgroundColor: isActive
      ? `${chipColor || theme.palette.primary[100]}14`
      : 'transparent',
    border: `1px solid ${
      isActive
        ? `${chipColor || theme.palette.primary[100]}40`
        : theme.palette.neutral[700]
    }`,
    transition: 'all 150ms linear',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: isActive
        ? `${chipColor || theme.palette.primary[100]}60`
        : theme.palette.neutral[600],
      backgroundColor: isActive
        ? `${chipColor || theme.palette.primary[100]}1A`
        : theme.palette.neutral[800]
    }
  })
)

export const NotificationsList = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column'
}))

export const DateGroup = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column'
}))

export const DateLabel = styled('div')(() => ({
  padding: '12px 16px 6px',
  fontSize: '11px',
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  color: theme.palette.neutral[500],
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
}))

export const ScrollSentinel = styled('div')(() => ({
  minHeight: '1px'
}))

export const EmptyState = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '80px 32px 64px',
  textAlign: 'center',
  gap: '16px'
}))

export const EmptyStateIcon = styled('div')(() => ({
  width: '56px',
  height: '56px',
  borderRadius: '14px',
  backgroundColor: theme.palette.neutral[800],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

export const SkeletonItem = styled('div')(() => ({
  display: 'flex',
  gap: '12px',
  padding: '14px 16px'
}))

export const SkeletonContent = styled('div')(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
}))
